import React, { useState } from 'react';
import { Col, Row, Card, Table, Input, Space, Modal } from 'antd';
import isEmpty from 'lodash/isEmpty';
import { RiAddLine, RiCloseFill } from 'react-icons/ri';
import { Search } from '@/lib/reactIconly';
import { Filter } from 'iconsax-react';
import { useIntl } from 'react-intl';
import { Link, useNavigate, useSearchParams } from 'react-router';
import basePagination from '../../../../../const/pagination';
import { Permissions } from '../../../../../const/permissions';
import appRoutes from '@/configs/routes';
import { PrivateComponent } from '../../../../../components/HasRights/HasRights';
import makeTableLoadingObject from '../../../../../lib/makeTableLoadingObject';
import useElementSize from '../../../../../app/hooks/useElementSize';
import useQueryParams from '../../../../../app/hooks/useQueryParams';
import useToggle from '../../../../../app/hooks/useToggle';
import useNavigationSource from '../../../../../app/hooks/useNavigationSource';
import getCategoriesOptions from '../../../../../lib/getCategoriesOptions';
import getSelectValues from '../../../../../lib/getSelectValues';
import handleResponse from '../../../../../lib/handleResponse';
import getConfigurations from '../../../../../lib/getConfigurations';
import resetFilter from '../../../../../lib/resetFilter';
import applyFilter from '../../../../../lib/applyFilter';
import DocumentationTooltip from '../../../../../components/DocumentationTooltip/DocumentationTooltip';
import hasActiveFilter from '@/lib/hasActiveFilter';
import { ActionButton, BreadCrumbs, IntlMessages } from '@/components';
import Sidebar from '../../../../../components/Sidebar';
import FormCollectionsFilter from '../../../forms/FormCollectionsFilter';
import PreviewDictionaryTranslation from '../../../../Translations/components/PreviewDictionaryTranslation/PreviewDictionaryTranslation';
import getFeatureSourceTranslation from '../../../../Featured/utils/getFeatureSourceTranslation';
import getEventTypeOptions from '../../../../../lib/getEventTypeOptions';
import { SOURCES_MAP } from '../../../../../const/sources';
import { COLLECTION_TYPES, getCollectionsTableColumns } from './PageCollections.const';
import { useGetLanguagesQuery } from '../../../../Translations/api/languagesApiSlice';
import { useDeleteCollectionMutation, useGetCollectionsQuery } from '../../../api/collectionsApiSlice';

const PageCollections = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [elementRef, size] = useElementSize();
  const [isVisibleFilterSidebar, toggleFilterSidebar] = useToggle();
  const [openDictionaryModal, toggleDictionaryModal] = useToggle();
  const [searchQueryParams, setSearchParams] = useSearchParams();
  const [previewDictionaryId, setPreviewDictionaryId] = useState({});

  const searchFields = ['title'];
  const searchFilterFields = [
    'id',
    'title',
    'template',
    'categories',
    'released_at',
    'released_to',
    'is_published',
    'image_type',
    'type',
  ];

  const collectionsConfig = getConfigurations(SOURCES_MAP.COLLECTIONS);
  const collectionType = searchQueryParams.get('type') ?? null;

  const { urlPrefix, navigationSource } = useNavigationSource(SOURCES_MAP.COLLECTIONS);
  const lockedTypeFilterValues = navigationSource ? { type: navigationSource } : {};

  const {
    pagination,
    search,
    setSearchTerm,
    searchParams,
    initFilterValues,
    setInitFilterValues,
    handleChangeTableParams,
    resetIsManuallyTriggered,
    setResetManuallyTriggered,
  } = useQueryParams({ searchFields, searchFilterFields });

  const searchParamsString = searchParams.toString();

  const {
    data = { data: [], pagination: {}, categories: [], image_types: [], types: [] },
    isFetching,
    error: formErrors = {},
  } = useGetCollectionsQuery({ queryParams: searchParamsString }, { skip: !searchParamsString });

  const categoriesOptions = getCategoriesOptions(data.categories);
  const imageTypeOptions = getSelectValues(data.image_types);
  const typeOptions = getEventTypeOptions(COLLECTION_TYPES, intl);

  const { data: { data: languages = [] } = { data: [] } } = useGetLanguagesQuery({ queryParams: 'limit=0' });

  const [deleteCollection, { isLoading: isDeletingCollection }] = useDeleteCollectionMutation();

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setResetManuallyTriggered(false);
  };

  const handlePreviewDictionary = (dictionaryId) => {
    setPreviewDictionaryId(dictionaryId);
    toggleDictionaryModal();
  };

  const handleDelete = (collectionId) => {
    deleteCollection(collectionId)
      .unwrap()
      .then(() => handleResponse('success', intl, 'ui-general-deleted-successfully'))
      .catch((error) => handleResponse('error', intl, 'ui-general-delete-failed', [], error));
  };

  const handleEdit = (collectionId) => {
    navigate(appRoutes.urlPageCollectionEdit({ urlPrefix, source: navigationSource, collectionId }));
  };

  const isLoading = isFetching || isDeletingCollection;

  return (
    <>
      <Modal
        title={<IntlMessages id="dictionaries-translation-preview" />}
        width="60%"
        centered
        destroyOnHidden
        open={openDictionaryModal}
        onCancel={toggleDictionaryModal}
        footer={null}
        closeIcon={<RiCloseFill className="remix-icon text-color-black-100" size={24} />}
      >
        <PreviewDictionaryTranslation dictionaryId={previewDictionaryId} languages={languages} />
      </Modal>

      <Row gutter={[32, 32]} justify="space-between" className="hp-mb-12" align="middle">
        <BreadCrumbs
          breadCrumbParent={
            urlPrefix ? (
              <Link to={appRoutes.urlPageFeatures({ featureSource: urlPrefix })}>
                <IntlMessages id={getFeatureSourceTranslation(urlPrefix)} />
              </Link>
            ) : null
          }
          breadCrumbActive={
            <div>
              <IntlMessages id="collections-breadcrumbs" />
              <DocumentationTooltip link="https://x-flow-ltd.atlassian.net/wiki/spaces/HCD/pages/951844867/Collections" />
            </div>
          }
        />

        <Col md={16} span={24}>
          <Row gutter={[32, 32]} justify="end" align="middle">
            <Col sm={12} span={24}>
              <Input
                data-testid="search-input"
                allowClear={!isEmpty(search)}
                placeholder={intl.formatMessage({ id: 'ui-general-search-by-title' })}
                prefix={<Search set="curved" size={16} className="hp-text-color-black-80" />}
                value={search}
                onChange={handleSearch}
              />
            </Col>
            <Col>
              <Space>
                <PrivateComponent allowedPermissions={[Permissions.CONTENT.COLLECTIONS.CREATE]}>
                  <ActionButton
                    onClick={() => navigate(appRoutes.urlPageCollectionCreate({ urlPrefix, source: navigationSource }))}
                    icon={<RiAddLine className="btn-icon-mr-1" />}
                    title={<IntlMessages id="ui-general-create" />}
                  />
                </PrivateComponent>
                <ActionButton
                  isActive={hasActiveFilter(searchQueryParams, searchFilterFields)}
                  icon={<Filter size={18} color="currentColor" data-testid="filter-action-icon1" />}
                  onClick={toggleFilterSidebar}
                />
              </Space>
            </Col>
          </Row>
        </Col>

        <Sidebar
          key={SOURCES_MAP.COLLECTIONS}
          title={intl.formatMessage({ id: 'collections-filter-title' })}
          visible={isVisibleFilterSidebar}
          toggleSidebar={toggleFilterSidebar}
        >
          <FormCollectionsFilter
            initialValues={{ ...initFilterValues, ...lockedTypeFilterValues }}
            formErrors={formErrors}
            isSubmitting={isFetching}
            imageTypeOptions={imageTypeOptions}
            typeOptions={typeOptions}
            isCollectionTypeDisabled={!isEmpty(collectionType)}
            defaultCollectionType={navigationSource}
            categoriesOptions={categoriesOptions}
            onCancel={() => {
              setResetManuallyTriggered(true);

              resetFilter({
                searchQueryParams,
                setSearchParams,
                setInitFilterValues,
                preservedSearch: navigationSource ? `type:${navigationSource}` : null,
                preservedInitFilterValues: lockedTypeFilterValues,
                resetIsManuallyTriggered,
                setSearchTerm,
              });
            }}
            onSubmit={(values) => {
              applyFilter({
                values: { ...values, ...lockedTypeFilterValues },
                searchQueryParams,
                searchFilterFields,
                setSearchParams,
                setSearchField: setSearchTerm,
                toggleFilterSidebar,
              });
            }}
          />
        </Sidebar>
      </Row>

      <Row gutter={[32, 32]}>
        <Col span={24}>
          <Card className="hp-border-color-black-40 hp-card-6">
            <div ref={elementRef}>
              <Table
                sticky
                scroll={{ x: 896 }}
                loading={makeTableLoadingObject(isLoading)}
                rowKey="id"
                columns={getCollectionsTableColumns(
                  handlePreviewDictionary,
                  handleEdit,
                  handleDelete,
                  collectionsConfig,
                  size,
                  urlPrefix,
                  navigationSource,
                )}
                dataSource={data.data}
                onChange={handleChangeTableParams}
                pagination={{
                  ...basePagination,
                  current: pagination.page,
                  pageSize: pagination.limit,
                  total: data.pagination?.total,
                }}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PageCollections;
